/**
 * Point 25 — a quotation with a line that makes no money does not get sent.
 *
 * ---- Why this is not the `noBelowCost` pricing rule ----
 *
 * `pricing.ts` already has one, and it is the wrong instrument here on two counts. It is a SETTING
 * (a company can switch it off, and then the gate does not exist), and it only fires BELOW cost, so
 * a line priced at exactly cost — the common way this actually happens, when a purchaser passes a
 * supplier or catalogue rate straight through and never sets a sell price — sails past it.
 *
 * This gate is unconditional and catches at-cost too. Akhil's ruling, 2026-09-03, taken with the
 * consequence stated: the in-house-stock and contract pass-through lines that `directPricing.ts`
 * and `contractQuotation.ts` create at margin 0 will now block the send until a selling price is
 * typed. That is the intended behaviour, not an oversight of it.
 *
 * ---- Amended 2026-09-07: an UNPRICED line is not a margin breach ----
 *
 * A line at 0.00 travels on purpose — it is the "nobody quoted this" state, and the quotation has
 * to reach the customer so the purchaser can source the item another way. Blocking it stopped
 * exactly the workflow the zero was built for. What point 25 was for is untouched: a line priced
 * below, or exactly at, a cost we know still blocks, because that gives margin away.
 *
 * ---- What it does NOT do ----
 *
 * It does not block on the quotation's OVERALL margin, and it does not stop anyone editing, saving
 * or previewing. It gates exactly one act — sending to the customer — which is the one that cannot
 * be taken back.
 */

import type { Money, QuotationLine, RuleViolation } from './types'

/** Below this the two amounts are the same price; float noise from FX and unit maths is not a margin. */
const EPSILON = 0.005

export function marginBlockers(lines: readonly QuotationLine[]): RuleViolation[] {
  const out: RuleViolation[] = []

  for (const line of lines) {
    const sell: Money = line.sell_unit
    const cost: Money = line.cost_unit
    const label = line.item_code || line.line_id

    /*
      An UNPRICED line is allowed through (Akhil, 2026-09-07) — this reverses half of point 25.

      A line at 0.00 is the deliberate "nobody quoted this yet" state `quotationFromAllocation.ts`
      creates on purpose, so the quotation can go out while the purchaser sources the item another
      way; the price follows later. Blocking it stopped the very workflow the zero exists for — on
      a real quotation, 7 lines of 109 refused the send.

      It is NOT treated as at-cost. Selling at cost is a priced decision that gives margin away;
      this is the absence of a decision, and the customer sees a line marked to be advised.
    */
    if (sell.amount <= EPSILON) continue

    // A cost of 0 means "we were never told what this cost", not "it was free". Any positive sell
    // price on such a line is margin as far as anything here can tell, and blocking it would
    // strand every line priced from a source that carries no cost figure.
    if (cost.amount <= EPSILON) continue

    // Bare-number comparison across currencies would read 120 USD against 100 AED as a healthy
    // margin when it is a loss. Unanswerable is not the same as fine — and an unverified margin is
    // precisely what this gate exists to keep off the wire.
    if (sell.currency !== cost.currency) {
      out.push({
        line_id: line.line_id,
        reason: `${label} is priced in ${sell.currency} against a ${cost.currency} cost — margin can't be checked`,
      })
      continue
    }

    if (sell.amount < cost.amount - EPSILON) {
      out.push({ line_id: line.line_id, reason: `${label} is below cost` })
    } else if (sell.amount <= cost.amount + EPSILON) {
      out.push({ line_id: line.line_id, reason: `${label} is at cost — no margin` })
    }
  }

  return out
}

/**
 * The one line of text shown under the send button. Names the items rather than counting them:
 * "3 lines have no margin" on a fifty-line quotation is a search task, and the purchaser is
 * standing at the one control that will not let them proceed.
 */
export function describeMarginBlockers(blockers: readonly RuleViolation[]): string | null {
  if (blockers.length === 0) return null
  const reasons = blockers.map((b) => b.reason)
  if (reasons.length === 1) return `Can't send: ${reasons[0]}. Set a selling price above cost first.`
  // Capped so a wholly unpriced quotation does not render its entire line list into the button's
  // caption; the count carries the rest.
  const shown = reasons.slice(0, 4).join('; ')
  const rest = reasons.length - Math.min(4, reasons.length)
  return `Can't send: ${blockers.length} lines have no margin — ${shown}${rest > 0 ? `, and ${rest} more` : ''}. Set selling prices above cost first.`
}

/**
 * The unpriced lines, named — a WARNING beside the send button, never a block.
 *
 * These travel at 0.00 on purpose: nobody quoted them, and the quotation still has to reach the
 * customer so the purchaser can source them another way (Akhil, 2026-09-07). What must not happen
 * is sending them unnoticed — a line the customer reads as free is worse than one marked "to be
 * advised". So the send proceeds and the purchaser is told exactly which lines are going out
 * without a price.
 *
 * Named rather than counted, and capped, for the same reasons as `describeMarginBlockers`.
 */
const UNPRICED_SHOWN = 4

export function describeUnpriced(lines: readonly QuotationLine[]): string | null {
  const unpriced = lines.filter((l) => l.sell_unit.amount <= EPSILON)
  if (unpriced.length === 0) return null

  const labels = unpriced.map((l) => l.item_code || l.line_id)
  const shown = labels.slice(0, UNPRICED_SHOWN).join(', ')
  const rest = labels.length - Math.min(UNPRICED_SHOWN, labels.length)

  return (
    `${unpriced.length} line${unpriced.length === 1 ? '' : 's'} will go out with NO price — ` +
    `${shown}${rest > 0 ? `, and ${rest} more` : ''}. Send only if the customer is meant to see them as to-be-advised.`
  )
}
